import { LitElement, html, css } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import { format, startOfDay, endOfDay, subDays } from "date-fns";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/badge/badge.js";
import "@shoelace-style/shoelace/dist/components/spinner/spinner.js";
import "@shoelace-style/shoelace/dist/components/dialog/dialog.js";
import "@shoelace-style/shoelace/dist/components/textarea/textarea.js";
import "../components/app-shell";
import "../components/cgm-graph";
import type {
  CgmReading,
  LogEntry,
  PeriodTag,
  UserSettings,
  DayNote,
} from "../types";
import {
  PERIOD_TAG_INFO,
  DIARY_ROW_LABELS,
  DAFNE_LABELS,
  formatInsulinDose,
  getQATotal,
  DEFAULT_SETTINGS,
} from "../types";
import {
  formatRatioDisplay,
  gramsPerUnitToUnitsPerCP,
  IS_BETA,
  BETA_WARNING,
} from "../config/dafne";
import { analyzePatterns, type PatternResult } from "../utils/pattern-analysis";
import {
  type HistoryDataAdapter,
  LocalHistoryAdapter,
} from "../services/history-data-adapter";
import { saveDayNote } from "../db/operations";
import { authService } from "../services/auth";
import { syncService } from "../services/sync";

type ViewPeriod = "3" | "7" | "14" | "30";
type TimeFilter = "all" | "morning" | "afternoon" | "evening" | "night";

interface DayStats {
  date: Date;
  readings: CgmReading[];
  avg: number;
  min: number;
  max: number;
  tir: number;
  hypos: number;
  highs: number;
}

@customElement("history-page")
export class HistoryPage extends LitElement {
  // Data adapter - provides data from different sources
  @property({ type: Object }) dataAdapter?: HistoryDataAdapter;

  @state() private allReadings: CgmReading[] = [];
  @state() private entries: LogEntry[] = [];
  @state() private dayNotes: DayNote[] = [];
  @state() private userSettings: UserSettings = DEFAULT_SETTINGS;
  @state() private loading = true;
  @state() private patterns: PatternResult[] = [];

  @state() private viewPeriod: ViewPeriod = "7";
  @state() private timeFilter: TimeFilter = "all";
  @state() private selectedDay: number | null = null;
  @state() private referenceDate: Date = new Date(); // The "end" date for the view
  @state() private dataDateRange: { start: Date; end: Date } | null = null;

  // Note editing state
  @state() private showNoteDialog = false;
  @state() private editingNoteDay: string | null = null; // yyyy-MM-dd
  @state() private noteText = "";
  @state() private savingNote = false;

  static styles = css`
    /* Box-sizing reset for shadow DOM */
    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    :host {
      display: block;
      width: 100%;
    }

    .page-header {
      padding: 1rem;
      background: #fff;
      border-bottom: 1px solid #e9ecef;
      display: none;
    }

    .page-header h1 {
      font-size: 1.25rem;
      font-weight: 600;
      margin: 0 0 0.25rem;
    }

    .page-header p {
      color: #6c757d;
      margin: 0;
      font-size: 0.875rem;
    }

    .back-header {
      padding: 0.5rem 1rem;
      background: #fff;
      border-bottom: 1px solid #e9ecef;
    }

    /* Show page header only on large screens where app-shell header is hidden */
    @media (min-width: 1024px) {
      .page-header {
        display: block;
      }
    }

    .view-tabs {
      display: flex;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      background: #f8f9fa;
      border-bottom: 1px solid #e9ecef;
    }

    .view-tabs sl-button::part(base) {
      font-weight: 500;
    }

    .time-filters {
      display: flex;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      background: #fff;
      border-bottom: 1px solid #e9ecef;
      flex-wrap: wrap;
    }

    .time-filters .label {
      font-size: 0.75rem;
      color: #6c757d;
      text-transform: uppercase;
      font-weight: 600;
      display: flex;
      align-items: center;
      margin-right: 0.5rem;
    }

    .graph-section {
      padding: 1rem;
      background: #fff;
      border-bottom: 1px solid #e9ecef;
    }

    .graph-scroll {
      display: block;
      width: 100%;
      overflow-x: auto;
      overflow-y: visible;
      -webkit-overflow-scrolling: touch;
      padding-bottom: 0.5rem;
    }

    .graph-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.5rem;
    }

    .graph-header h3 {
      font-size: 0.875rem;
      font-weight: 600;
      margin: 0;
      color: #495057;
    }

    .stats-row {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 0.5rem;
      padding: 1rem;
      background: #fff;
      border-bottom: 1px solid #e9ecef;
    }

    .stat-card {
      text-align: center;
      padding: 0.75rem 0.5rem;
      background: #f8f9fa;
      border-radius: 0.5rem;
    }

    .stat-value {
      font-size: 1.25rem;
      font-weight: 700;
      color: #212529;
    }

    .stat-value.good {
      color: #198754;
    }

    .stat-value.warning {
      color: #fd7e14;
    }

    .stat-value.bad {
      color: #dc3545;
    }

    .stat-label {
      font-size: 0.7rem;
      color: #6c757d;
      text-transform: uppercase;
      margin-top: 0.25rem;
    }

    .daily-breakdown {
      padding: 1rem;
    }

    .daily-breakdown h3 {
      font-size: 0.875rem;
      font-weight: 600;
      margin: 0 0 0.75rem;
      color: #495057;
    }

    .day-row {
      display: grid;
      grid-template-columns: 70px 1fr repeat(4, 38px);
      gap: 0.25rem;
      padding: 0.5rem;
      background: #fff;
      border: 1px solid #e9ecef;
      border-radius: 0.5rem;
      margin-bottom: 0.5rem;
      align-items: center;
      cursor: pointer;
      transition: all 0.15s;
    }

    .day-row:hover {
      background: #f8f9fa;
      border-color: #dee2e6;
    }

    .day-row.selected {
      background: #e7f1ff;
      border-color: #0d6efd;
    }

    .day-row.no-data {
      opacity: 0.5;
      cursor: default;
    }

    .day-date {
      font-weight: 500;
      font-size: 0.75rem;
    }

    .day-date .weekday {
      font-size: 0.65rem;
      color: #6c757d;
      font-weight: normal;
    }

    .day-sparkline {
      height: 20px;
      background: #f8f9fa;
      border-radius: 0.25rem;
      overflow: hidden;
      min-width: 60px;
    }

    .day-stat {
      font-size: 0.7rem;
      text-align: center;
      font-weight: 500;
    }

    .day-stat.avg {
      color: #212529;
    }

    .day-stat.tir {
      color: #198754;
    }

    .day-stat.hypo {
      color: #dc3545;
    }

    .day-stat.high {
      color: #fd7e14;
    }

    .day-detail {
      padding: 1rem;
      background: #f8f9fa;
      margin: -0.5rem 0 0.5rem;
      border-radius: 0 0 0.5rem 0.5rem;
      border: 1px solid #e9ecef;
      border-top: none;
    }

    .readings-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.8rem;
    }

    .readings-table th {
      text-align: left;
      padding: 0.5rem;
      border-bottom: 1px solid #dee2e6;
      color: #6c757d;
      font-weight: 500;
    }

    .readings-table td {
      padding: 0.5rem;
      border-bottom: 1px solid #f0f0f0;
    }

    .reading-badge {
      display: inline-block;
      padding: 0.2rem 0.4rem;
      border-radius: 0.25rem;
      font-weight: 600;
      font-size: 0.75rem;
    }

    .reading-badge.hypo {
      background: #f8d7da;
      color: #842029;
    }

    .reading-badge.low {
      background: #fff3cd;
      color: #664d03;
    }

    .reading-badge.target {
      background: #d1e7dd;
      color: #0f5132;
    }

    .reading-badge.elevated {
      background: #ffe5d0;
      color: #984c0c;
    }

    .reading-badge.high {
      background: #f8d7da;
      color: #842029;
    }

    .no-data {
      text-align: center;
      padding: 3rem 1rem;
      color: #6c757d;
    }

    .no-data sl-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
      display: block;
    }

    .loading {
      display: flex;
      justify-content: center;
      padding: 3rem;
    }

    .summary-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 0.75rem;
    }

    .summary-header h3 {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: #495057;
    }

    .day-header-row {
      display: grid;
      grid-template-columns: 70px 1fr repeat(4, 38px);
      gap: 0.25rem;
      padding: 0.5rem;
      font-size: 0.65rem;
      color: #6c757d;
      text-transform: uppercase;
      font-weight: 600;
    }

    /* DAFNE Diary Table Styles */
    .diary-section {
      padding: 1rem;
      background: #fff;
      width: 100%;
      overflow-x: auto;
    }

    .diary-section h3 {
      font-size: 0.875rem;
      font-weight: 600;
      margin: 0 0 0.75rem;
      color: #495057;
    }

    .table-scroll {
      display: block;
      width: 100%;
      overflow-x: auto;
      overflow-y: visible;
      -webkit-overflow-scrolling: touch;
      padding-bottom: 0.5rem;
    }

    .dafne-diary {
      min-width: 700px;
      border-collapse: collapse;
      font-size: 0.75rem;
      border: 1px solid #dee2e6;
    }

    .dafne-diary th {
      background: #f8f9fa;
      padding: 0.5rem 0.5rem;
      text-align: center;
      font-weight: 600;
      color: #495057;
      border: 1px solid #dee2e6;
      white-space: nowrap;
    }

    .dafne-diary th.date-col {
      width: 80px;
      min-width: 80px;
      text-align: left;
      padding-left: 0.5rem;
    }

    .dafne-diary th.field-col {
      width: 50px;
      min-width: 50px;
    }

    .dafne-diary th.period-col {
      min-width: 55px;
      width: auto;
    }

    .dafne-diary td {
      padding: 0.35rem 0.5rem;
      text-align: right;
      border: 1px solid #e9ecef;
      white-space: nowrap;
    }

    .dafne-diary .date-cell {
      text-align: left;
      padding-left: 0.5rem;
      background: #f8f9fa;
      vertical-align: top;
    }

    .dafne-diary .day-name {
      font-weight: 600;
      color: #212529;
    }

    .dafne-diary .day-date {
      font-size: 0.7rem;
      color: #6c757d;
    }

    .dafne-diary .field-label {
      text-align: left;
      padding-left: 0.25rem;
      font-weight: 500;
      color: #6c757d;
      background: #fafafa;
    }

    .dafne-diary .day-start td {
      border-top: 2px solid #dee2e6;
    }

    .dafne-diary .data-cell {
      min-width: 55px;
    }

    .dafne-diary .empty {
      color: #dee2e6;
    }

    .dafne-diary .gl-hypo {
      color: #842029;
      background: #f8d7da;
      border-radius: 0.25rem;
      padding: 0.1rem 0.25rem;
    }

    .dafne-diary .gl-low {
      color: #664d03;
      background: #fff3cd;
      border-radius: 0.25rem;
      padding: 0.1rem 0.25rem;
    }

    .dafne-diary .gl-target {
      color: #0f5132;
      background: #d1e7dd;
      border-radius: 0.25rem;
      padding: 0.1rem 0.25rem;
    }

    .dafne-diary .gl-elevated {
      color: #984c0c;
      background: #ffe5d0;
      border-radius: 0.25rem;
      padding: 0.1rem 0.25rem;
    }

    .dafne-diary .gl-high {
      color: #842029;
      background: #f8d7da;
      border-radius: 0.25rem;
      padding: 0.1rem 0.25rem;
    }

    .dafne-diary .cgm-fallback {
      font-style: italic;
      opacity: 0.8;
    }

    .dafne-diary .ratio-row td {
      background: #e7f1ff;
      font-size: 0.7rem;
      padding: 0.25rem 0.5rem;
      color: #0d6efd;
      font-weight: 500;
      text-align: center;
      border-top: 2px solid #0d6efd;
      border-bottom: 2px solid #0d6efd;
    }

    .dafne-diary .ratio-row td.ratio-label {
      text-align: left;
      padding-left: 0.5rem;
      font-weight: 600;
    }

    .dafne-diary .ratio-row td.correction-cell {
      background: #fff3cd;
      color: #664d03;
      border-top-color: #ffc107;
      border-bottom-color: #ffc107;
    }

    .ratio-header {
      background: #e7f1ff;
      padding: 0.75rem 1rem;
      margin-bottom: 0.5rem;
      border-radius: 0.375rem;
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;
      align-items: center;
    }

    .ratio-header .ratio-title {
      font-weight: 600;
      font-size: 0.75rem;
      color: #0d6efd;
      text-transform: uppercase;
    }

    .ratio-header .ratio-item {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.8rem;
    }

    .ratio-header .ratio-period {
      font-weight: 600;
      color: #495057;
    }

    .ratio-header .ratio-value {
      color: #0d6efd;
      font-weight: 500;
    }

    .ratio-header .correction-item {
      background: #fff3cd;
      padding: 0.25rem 0.5rem;
      border-radius: 0.25rem;
      color: #664d03;
    }

    /* Day Notes Styles */
    .day-notes-row td {
      padding: 0.75rem 1rem;
      background: #f8f9fa;
      border-top: 1px dashed #dee2e6;
    }

    .day-notes-container {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .day-note {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      padding: 0.5rem;
      background: #fff;
      border-radius: 0.375rem;
      border: 1px solid #e9ecef;
    }

    .day-note.personal-note {
      border-left: 3px solid #0d6efd;
    }

    .day-note.clinic-note {
      border-left: 3px solid #198754;
      background: #d1e7dd;
    }

    .day-note-header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.75rem;
      color: #6c757d;
    }

    .day-note-header .note-type {
      font-weight: 600;
      text-transform: uppercase;
    }

    .day-note-header .note-author {
      color: #495057;
    }

    .day-note-header .note-date {
      margin-left: auto;
    }

    .day-note-content {
      font-size: 0.875rem;
      color: #212529;
      white-space: pre-wrap;
    }

    .add-note-btn {
      font-size: 0.75rem;
      --sl-input-height-small: 1.5rem;
    }

    .note-dialog-date {
      margin: 0 0 1rem;
      color: #6c757d;
      font-size: 0.875rem;
    }

    .no-entries-msg {
      text-align: center;
      padding: 2rem 1rem;
      color: #6c757d;
      background: #f8f9fa;
      border-radius: 0.5rem;
    }

    /* Interleaved Day Card Styles */
    .day-card {
      background: #fff;
      border: 1px solid #e9ecef;
      border-radius: 0.5rem;
      margin: 0 1rem 1rem;
      overflow: hidden;
    }

    .day-card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 1rem;
      background: #f8f9fa;
      border-bottom: 1px solid #e9ecef;
    }

    .day-card-header .day-title {
      font-weight: 600;
      font-size: 0.9rem;
    }

    .day-card-header .day-date-label {
      font-size: 0.75rem;
      color: #6c757d;
    }

    .day-card-body {
      padding: 0;
    }

    .day-diary-section {
      padding: 0.75rem;
      border-bottom: 1px solid #f0f0f0;
    }

    .day-diary-section .section-label {
      font-size: 0.7rem;
      color: #6c757d;
      text-transform: uppercase;
      font-weight: 600;
      margin-bottom: 0.5rem;
    }

    .day-diary-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.75rem;
    }

    .day-diary-table th {
      background: #f8f9fa;
      padding: 0.375rem 0.25rem;
      text-align: center;
      font-weight: 600;
      color: #495057;
      border-bottom: 1px solid #dee2e6;
    }

    .day-diary-table td {
      padding: 0.25rem;
      text-align: center;
      border-bottom: 1px solid #f0f0f0;
    }

    .day-diary-table .field-label {
      text-align: left;
      padding-left: 0.25rem;
      font-weight: 500;
      color: #6c757d;
      background: #fafafa;
      width: 50px;
    }

    .day-cgm-section {
      padding: 0.75rem;
      background: #fafafa;
    }

    .day-cgm-section .section-label {
      font-size: 0.7rem;
      color: #6c757d;
      text-transform: uppercase;
      font-weight: 600;
      margin-bottom: 0.5rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .day-cgm-stats {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 0.5rem;
      margin-top: 0.5rem;
    }

    .day-cgm-stats .cgm-stat {
      text-align: center;
      padding: 0.5rem;
      background: #fff;
      border-radius: 0.25rem;
      border: 1px solid #e9ecef;
    }

    .day-cgm-stats .cgm-stat-value {
      font-size: 1rem;
      font-weight: 600;
    }

    .day-cgm-stats .cgm-stat-label {
      font-size: 0.625rem;
      color: #6c757d;
      text-transform: uppercase;
    }

    .day-cgm-stats .good {
      color: #198754;
    }
    .day-cgm-stats .warning {
      color: #fd7e14;
    }
    .day-cgm-stats .bad {
      color: #dc3545;
    }

    .day-sparkline-large {
      height: 50px;
      background: #fff;
      border-radius: 0.25rem;
      border: 1px solid #e9ecef;
      overflow: hidden;
    }

    .no-diary-entries {
      font-size: 0.75rem;
      color: #adb5bd;
      font-style: italic;
      padding: 0.5rem 0;
    }

    .no-cgm-data {
      font-size: 0.75rem;
      color: #adb5bd;
      font-style: italic;
      padding: 0.5rem 0;
      text-align: center;
    }

    /* DAFNE Pattern Analysis Styles */
    .patterns-section {
      padding: 1rem;
      background: #fff;
      border-bottom: 1px solid #e9ecef;
    }

    .patterns-section h3 {
      font-size: 0.875rem;
      font-weight: 600;
      margin: 0 0 0.75rem;
      color: #495057;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    .pattern-cards {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      width: 100%;
    }

    .pattern-card {
      border: 1px solid #e9ecef;
      border-radius: 0.5rem;
      overflow: hidden;
      width: 100%;
    }

    .pattern-card.severity-action {
      border-color: #f5c6cb;
    }

    .pattern-card.severity-warning {
      border-color: #ffeeba;
    }

    .pattern-card.severity-info {
      border-color: #b8daff;
    }

    .pattern-header {
      padding: 0.75rem;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }

    .severity-action .pattern-header {
      background: #f8d7da;
    }

    .severity-warning .pattern-header {
      background: #fff3cd;
    }

    .severity-info .pattern-header {
      background: #d1ecf1;
    }

    .pattern-title {
      font-weight: 600;
      font-size: 0.9rem;
      margin: 0;
    }

    .pattern-badge {
      font-size: 0.65rem;
      padding: 0.2rem 0.5rem;
      border-radius: 0.25rem;
      font-weight: 600;
      text-transform: uppercase;
    }

    .severity-action .pattern-badge {
      background: #dc3545;
      color: white;
    }

    .severity-warning .pattern-badge {
      background: #ffc107;
      color: #212529;
    }

    .severity-info .pattern-badge {
      background: #17a2b8;
      color: white;
    }

    .pattern-body {
      padding: 0.75rem;
      background: #fff;
    }

    .pattern-detail {
      font-size: 0.8rem;
      color: #495057;
      margin: 0 0 0.75rem;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .pattern-recommendation {
      font-size: 0.8rem;
      background: #f8f9fa;
      padding: 0.75rem;
      border-radius: 0.375rem;
      border-left: 3px solid #0d6efd;
      word-wrap: break-word;
      overflow-wrap: break-word;
    }

    .pattern-recommendation strong {
      display: block;
      font-size: 0.7rem;
      text-transform: uppercase;
      color: #0d6efd;
      margin-bottom: 0.25rem;
    }

    .pattern-affected {
      display: flex;
      gap: 0.25rem;
      margin-top: 0.5rem;
      flex-wrap: wrap;
    }

    .pattern-period-tag {
      font-size: 0.65rem;
      padding: 0.15rem 0.4rem;
      background: #e9ecef;
      border-radius: 0.25rem;
      color: #495057;
    }

    .no-patterns {
      text-align: center;
      padding: 1.5rem;
      color: #6c757d;
      background: #f8f9fa;
      border-radius: 0.5rem;
      font-size: 0.85rem;
    }

    .no-patterns sl-icon {
      font-size: 2rem;
      margin-bottom: 0.5rem;
      display: block;
    }
  `;

  async connectedCallback() {
    super.connectedCallback();
    // Sync first if online and viewing own data (no adapter means own data)
    if (navigator.onLine && !this.dataAdapter) {
      await syncService.checkAndSync();
    }
    await this._loadData();
  }

  private async _loadData() {
    this.loading = true;
    try {
      // Use adapter if provided, otherwise use default local adapter
      const adapter = this.dataAdapter || new LocalHistoryAdapter();
      const { readings, entries, settings, dayNotes } =
        await adapter.loadData();

      this.allReadings = readings;
      this.entries = entries;
      this.dayNotes = dayNotes || [];
      this.userSettings = settings || DEFAULT_SETTINGS;

      // Calculate data date range from both CGM readings and entries
      let minTs = Infinity;
      let maxTs = -Infinity;

      for (const r of readings) {
        const ts = this._getTimestamp(r.timestamp);
        if (ts < minTs) minTs = ts;
        if (ts > maxTs) maxTs = ts;
      }

      for (const e of entries) {
        const ts = this._getTimestamp(e.timestamp);
        if (ts < minTs) minTs = ts;
        if (ts > maxTs) maxTs = ts;
      }

      if (minTs !== Infinity && maxTs !== -Infinity) {
        this.dataDateRange = {
          start: new Date(minTs),
          end: new Date(maxTs),
        };
        // Use today if within data range, otherwise use latest data date
        const today = new Date();
        if (today.getTime() <= maxTs) {
          this.referenceDate = today;
        } else {
          this.referenceDate = new Date(maxTs);
        }
      }

      // Run pattern analysis
      this._updatePatterns();
    } catch (error) {
      console.error("Failed to load data:", error);
    }
    this.loading = false;
  }

  private _updatePatterns() {
    this.patterns = analyzePatterns({
      cgmReadings: this.allReadings,
      entries: this.entries,
      daysToAnalyze: parseInt(this.viewPeriod),
      targetLow: 4,
      targetHigh: 10,
    });
  }

  private _getTimestamp(t: Date | string | number | unknown): number {
    if (t instanceof Date) return t.getTime();
    if (typeof t === "string") return new Date(t).getTime();
    if (typeof t === "number") return t;
    return new Date(t as string).getTime();
  }

  /**
   * Categorize a 3AM-tagged entry by its actual time:
   * - "3am": 3:00-3:59 (true 3am)
   * - "night": 00:00-2:59 (pre-3am night entries)
   * - "early": 4:00+ (after 3am, should use different period tag)
   */
  private _get3amTimeCategory(
    timestamp: Date | string | number | unknown,
  ): "3am" | "night" | "early" {
    const date = new Date(this._getTimestamp(timestamp));
    const hour = date.getHours();
    if (hour === 3) return "3am";
    if (hour < 3) return "night";
    return "early";
  }

  private _getFilteredReadings(): CgmReading[] {
    const days = parseInt(this.viewPeriod);
    const refDate = this.referenceDate;
    const startTs = startOfDay(subDays(refDate, days - 1)).getTime();
    const endTs = endOfDay(refDate).getTime();

    return this.allReadings.filter((r) => {
      const ts = this._getTimestamp(r.timestamp);
      if (ts < startTs || ts > endTs) return false;

      // Time of day filter
      if (this.timeFilter !== "all") {
        const date = new Date(ts);
        const hour = date.getHours();

        switch (this.timeFilter) {
          case "morning": // 6-12
            if (hour < 6 || hour >= 12) return false;
            break;
          case "afternoon": // 12-18
            if (hour < 12 || hour >= 18) return false;
            break;
          case "evening": // 18-22
            if (hour < 18 || hour >= 22) return false;
            break;
          case "night": // 22-6
            if (hour >= 6 && hour < 22) return false;
            break;
        }
      }

      return true;
    });
  }

  private _getReadingsForDay(dayOffset: number): CgmReading[] {
    const dayDate = subDays(this.referenceDate, dayOffset);
    const dayStart = startOfDay(dayDate).getTime();
    const dayEnd = endOfDay(dayDate).getTime();

    return this.allReadings
      .filter((r) => {
        const ts = this._getTimestamp(r.timestamp);
        if (ts < dayStart || ts > dayEnd) return false;

        // Apply time filter
        if (this.timeFilter !== "all") {
          const date = new Date(ts);
          const hour = date.getHours();

          switch (this.timeFilter) {
            case "morning":
              if (hour < 6 || hour >= 12) return false;
              break;
            case "afternoon":
              if (hour < 12 || hour >= 18) return false;
              break;
            case "evening":
              if (hour < 18 || hour >= 22) return false;
              break;
            case "night":
              if (hour >= 6 && hour < 22) return false;
              break;
          }
        }

        return true;
      })
      .sort(
        (a, b) =>
          this._getTimestamp(a.timestamp) - this._getTimestamp(b.timestamp),
      );
  }

  /**
   * Get the CGM reading closest to 3AM for a given day.
   * Used as fallback when no DAFNE entry exists for the 3AM period.
   * Returns null if no CGM readings exist for that day.
   */
  private _get3amCgmReading(dayOffset: number): CgmReading | null {
    const dayDate = subDays(this.referenceDate, dayOffset);
    const target3am = new Date(dayDate);
    target3am.setHours(3, 0, 0, 0);
    const target3amTs = target3am.getTime();

    // Get all readings for the day
    const dayStart = startOfDay(dayDate).getTime();
    const dayEnd = endOfDay(dayDate).getTime();

    const dayReadings = this.allReadings.filter((r) => {
      const ts = this._getTimestamp(r.timestamp);
      return ts >= dayStart && ts <= dayEnd;
    });

    if (dayReadings.length === 0) return null;

    // Find closest reading to 3AM
    let closest: CgmReading | null = null;
    let minDiff = Infinity;

    for (const reading of dayReadings) {
      const ts = this._getTimestamp(reading.timestamp);
      const diff = Math.abs(ts - target3amTs);
      if (diff < minDiff) {
        minDiff = diff;
        closest = reading;
      }
    }

    return closest;
  }

  private _calculatePeriodStats(readings: CgmReading[]) {
    if (readings.length === 0) {
      return { avg: 0, min: 0, max: 0, tir: 0, hypos: 0, highs: 0, count: 0 };
    }

    const values = readings.map((r) => r.glucose);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const inRange = values.filter((v) => v >= 4 && v <= 10).length;

    return {
      avg,
      min: Math.min(...values),
      max: Math.max(...values),
      tir: Math.round((inRange / values.length) * 100),
      hypos: values.filter((v) => v < 4).length,
      highs: values.filter((v) => v > 10).length,
      count: values.length,
    };
  }

  private _getDayStats(dayOffset: number): DayStats {
    const readings = this._getReadingsForDay(dayOffset);
    const stats = this._calculatePeriodStats(readings);

    return {
      date: subDays(this.referenceDate, dayOffset),
      readings,
      ...stats,
    };
  }

  private _getGlClass(value?: number): string {
    if (!value) return "";
    if (value < 4) return "gl-hypo";
    if (value < 5) return "gl-low";
    if (value <= 7) return "gl-target";
    if (value <= 10) return "gl-elevated";
    return "gl-high";
  }

  private _getEntriesForDay(dayOffset: number): LogEntry[] {
    const dayDate = subDays(this.referenceDate, dayOffset);
    const dayStart = startOfDay(dayDate);
    const dayEnd = endOfDay(dayDate);

    return this.entries
      .filter((e) => {
        const ts = this._getTimestamp(e.timestamp);
        return ts >= dayStart.getTime() && ts <= dayEnd.getTime();
      })
      .sort(
        (a, b) =>
          this._getTimestamp(a.timestamp) - this._getTimestamp(b.timestamp),
      );
  }

  private _getNotesForDay(dayOffset: number): DayNote[] {
    const dayDate = subDays(this.referenceDate, dayOffset);
    const dateKey = format(dayDate, "yyyy-MM-dd");

    return this.dayNotes
      .filter((n) => n.date === dateKey)
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
  }

  private _openAddNoteDialog(dayOffset: number) {
    const dayDate = subDays(this.referenceDate, dayOffset);
    this.editingNoteDay = format(dayDate, "yyyy-MM-dd");
    this.noteText = "";
    this.showNoteDialog = true;
  }

  private _closeNoteDialog() {
    this.showNoteDialog = false;
    this.editingNoteDay = null;
    this.noteText = "";
  }

  private async _handleSaveNote() {
    if (!this.editingNoteDay || !this.noteText.trim()) return;

    const authState = await authService.getCurrentUser();
    if (!authState.isAuthenticated || !authState.userId) return;

    this.savingNote = true;
    try {
      // Use adapter's saveNote if available (for monitored users)
      if (this.dataAdapter?.saveNote) {
        await this.dataAdapter.saveNote(
          this.editingNoteDay,
          this.noteText.trim(),
        );
      } else {
        // Save to local database (own data)
        await saveDayNote({
          date: this.editingNoteDay,
          type: "personal",
          content: this.noteText.trim(),
          authorId: authState.userId,
          authorName: authState.displayName || authState.email || "Me",
        });
      }

      // Refresh day notes
      const adapter = this.dataAdapter || new LocalHistoryAdapter();
      const data = await adapter.loadData();
      this.dayNotes = data.dayNotes || [];

      this._closeNoteDialog();
    } catch (error) {
      console.error("Failed to save note:", error);
    } finally {
      this.savingNote = false;
    }
  }

  private _handleViewChange(period: ViewPeriod) {
    this.viewPeriod = period;
    this.selectedDay = null;
    this._updatePatterns();
  }

  private _handleTimeFilter(filter: TimeFilter) {
    this.timeFilter = filter;
  }

  private _renderStats() {
    const readings = this._getFilteredReadings();
    const stats = this._calculatePeriodStats(readings);

    if (readings.length === 0) {
      return "";
    }

    return html`
      <div class="stats-row">
        <div class="stat-card">
          <div
            class="stat-value ${stats.avg >= 5 && stats.avg <= 8
              ? "good"
              : stats.avg < 4 || stats.avg > 12
                ? "bad"
                : "warning"}"
          >
            ${stats.avg.toFixed(1)}
          </div>
          <div class="stat-label">Avg mmol/L</div>
        </div>
        <div class="stat-card">
          <div
            class="stat-value ${stats.tir >= 70
              ? "good"
              : stats.tir >= 50
                ? "warning"
                : "bad"}"
          >
            ${stats.tir}%
          </div>
          <div class="stat-label">TIR 4-10</div>
        </div>
        <div class="stat-card">
          <div class="stat-value ${stats.hypos === 0 ? "good" : "bad"}">
            ${stats.hypos}
          </div>
          <div class="stat-label">Hypos &lt;4</div>
        </div>
        <div class="stat-card">
          <div
            class="stat-value ${stats.highs <= 5
              ? "good"
              : stats.highs <= 15
                ? "warning"
                : "bad"}"
          >
            ${stats.highs}
          </div>
          <div class="stat-label">Highs &gt;10</div>
        </div>
      </div>
    `;
  }

  private _renderPatterns() {
    if (this.patterns.length === 0) {
      return html`
        <div class="patterns-section">
          <h3>
            <sl-icon name="clipboard-data"></sl-icon>
            DAFNE Pattern Analysis
          </h3>
          <div class="no-patterns">
            <sl-icon name="check-circle"></sl-icon>
            <p>No significant patterns detected.</p>
            <p>Continue logging to enable pattern detection.</p>
          </div>
        </div>
      `;
    }

    return html`
      <div class="patterns-section">
        <h3>
          <sl-icon name="clipboard-data"></sl-icon>
          DAFNE Pattern Analysis
          <sl-badge variant="primary" pill>${this.patterns.length}</sl-badge>
        </h3>
        ${IS_BETA
          ? html`<div
              class="beta-warning"
              style="margin-bottom: 1rem; font-size: 0.8rem; color: #856404; background: #fff3cd; padding: 0.5rem 0.75rem; border-radius: 6px; border: 1px solid #ffeeba;"
            >
              ⚠️ <strong>Beta:</strong> ${BETA_WARNING.SHORT}
            </div>`
          : ""}
        <div class="pattern-cards">
          ${this.patterns.map(
            (pattern) => html`
              <div class="pattern-card severity-${pattern.severity}">
                <div class="pattern-header">
                  <p class="pattern-title">${pattern.title}</p>
                  <span class="pattern-badge">${pattern.severity}</span>
                </div>
                <div class="pattern-body">
                  <p class="pattern-detail">${pattern.detail}</p>
                  <div class="pattern-recommendation">
                    <strong>DAFNE Action</strong>
                    ${pattern.dafneRecommendation}
                  </div>
                  ${pattern.affectedPeriods &&
                  pattern.affectedPeriods.length > 0
                    ? html`
                        <div class="pattern-affected">
                          ${pattern.affectedPeriods.map(
                            (period) => html`
                              <span class="pattern-period-tag">
                                ${PERIOD_TAG_INFO[period]?.label || period}
                              </span>
                            `,
                          )}
                        </div>
                      `
                    : ""}
                </div>
              </div>
            `,
          )}
        </div>
      </div>
    `;
  }

  private _renderGraph() {
    const readings = this._getFilteredReadings();
    if (readings.length === 0) return "";

    const days = parseInt(this.viewPeriod);
    const dateRange = {
      start: startOfDay(subDays(this.referenceDate, days - 1)),
      end: endOfDay(this.referenceDate),
    };

    // Get entries for the period
    const startTs = dateRange.start.getTime();
    const endTs = dateRange.end.getTime();
    const periodEntries = this.entries.filter((e) => {
      const ts = this._getTimestamp(e.timestamp);
      return ts >= startTs && ts <= endTs;
    });

    return html`
      <div class="graph-section">
        <div class="graph-header">
          <h3>Glucose Trend - Last ${this.viewPeriod} Days</h3>
          <sl-badge variant="neutral">${readings.length} readings</sl-badge>
        </div>
        <div class="graph-scroll">
          <cgm-graph
            .readings=${readings}
            .entries=${periodEntries}
            .dateRange=${dateRange}
            height=${200}
            hasAnyData
          ></cgm-graph>
        </div>
      </div>
    `;
  }

  private _buildDynamicColumns(): {
    period: PeriodTag;
    index: number;
    label: string;
    timeCategory?: "3am" | "night" | "early"; // For 3AM period only
  }[] {
    const days = parseInt(this.viewPeriod);
    const periodGroups: PeriodTag[][] = [
      ["3AM"],
      ["B", "after-B"],
      ["L", "after-L"],
      ["EM", "after-EM"],
      ["BT"],
    ];

    // Core periods that should always be shown (like a paper diary)
    // 3AM is mandatory per OZDAFNE for fasting glucose tracking
    const corePeriods: Set<PeriodTag> = new Set(["3AM", "B", "L", "EM", "BT"]);

    // Count max entries per period across selected days
    // For 3AM, we count by time category separately
    const periodMaxCounts: Map<PeriodTag, number> = new Map();
    let maxNightCount = 0; // 00:00-2:59 entries tagged 3AM
    let maxEarlyCount = 0; // 4:00+ entries tagged 3AM

    for (let dayOffset = 0; dayOffset < days; dayOffset++) {
      const dayEntries = this._getEntriesForDay(dayOffset);
      const periodCounts: Map<PeriodTag, number> = new Map();
      let nightCount = 0;
      let earlyCount = 0;

      for (const entry of dayEntries) {
        // Special handling for 3AM-tagged entries - categorize by actual time
        if (entry.periodTag === "3AM" && entry.timestamp) {
          const category = this._get3amTimeCategory(entry.timestamp);
          if (category === "night") {
            nightCount++;
          } else if (category === "early") {
            earlyCount++;
          }
          // true 3am (3:00-3:59) entries count toward the main 3AM period
          // but we only need one column for it
        } else {
          const count = (periodCounts.get(entry.periodTag) || 0) + 1;
          periodCounts.set(entry.periodTag, count);
        }
      }

      // Update max counts for night/early 3AM entries
      if (nightCount > maxNightCount) maxNightCount = nightCount;
      if (earlyCount > maxEarlyCount) maxEarlyCount = earlyCount;

      for (const [period, count] of periodCounts) {
        const current = periodMaxCounts.get(period) || 0;
        if (count > current) {
          periodMaxCounts.set(period, count);
        }
      }
    }

    // Build columns
    const columns: {
      period: PeriodTag;
      index: number;
      label: string;
      timeCategory?: "3am" | "night" | "early";
    }[] = [];

    for (const group of periodGroups) {
      for (const period of group) {
        if (period === "3AM") {
          // Add night columns (00:00-2:59) before the 3am column
          for (let i = 0; i < maxNightCount; i++) {
            columns.push({
              period: "3AM",
              index: i,
              label: i === 0 ? "Night" : `Night′`,
              timeCategory: "night",
            });
          }

          // Always add the true "3am" column (3:00-3:59)
          columns.push({
            period: "3AM",
            index: 0,
            label: "3am",
            timeCategory: "3am",
          });

          // Add early columns (4:00+) after the 3am column - unusual but handle it
          for (let i = 0; i < maxEarlyCount; i++) {
            columns.push({
              period: "3AM",
              index: i,
              label: i === 0 ? "Early" : `Early′`,
              timeCategory: "early",
            });
          }
        } else {
          const maxCount = periodMaxCounts.get(period) || 0;
          // Include core periods even if they have no entries (consistent book appearance)
          const isCorePeriod = corePeriods.has(period);
          if (maxCount === 0 && !isCorePeriod) continue;

          const baseLabel = PERIOD_TAG_INFO[period].label.split(" ")[0];

          if (maxCount <= 1) {
            columns.push({ period, index: 0, label: baseLabel });
          } else {
            for (let i = 0; i < maxCount; i++) {
              columns.push({
                period,
                index: i,
                label: i === 0 ? baseLabel : `${baseLabel}′`,
              });
            }
          }
        }
      }
    }

    return columns;
  }

  private _toggleDayDetail(dayOffset: number, hasData: boolean) {
    if (!hasData) return;
    this.selectedDay = this.selectedDay === dayOffset ? null : dayOffset;
  }

  private _getReadingClass(value: number): string {
    if (value < 4) return "hypo";
    if (value < 5) return "low";
    if (value <= 7) return "target";
    if (value <= 10) return "elevated";
    return "high";
  }

  private _renderDiaryTable() {
    const days = parseInt(this.viewPeriod);
    const columns = this._buildDynamicColumns();
    const dataRows = [...DIARY_ROW_LABELS];
    const settings = this.userSettings;

    // Get unique periods that have ratio columns (exclude 3AM which doesn't have CP ratio)
    const ratioPeriods = ["B", "L", "EM"] as const;

    // Build a list of entries sorted by time to detect ratio changes
    const allEntriesInPeriod = this.entries
      .filter((e) => {
        const ts = this._getTimestamp(e.timestamp);
        const start = startOfDay(
          subDays(this.referenceDate, days - 1),
        ).getTime();
        const end = endOfDay(this.referenceDate).getTime();
        return ts >= start && ts <= end;
      })
      .sort(
        (a, b) =>
          this._getTimestamp(b.timestamp) - this._getTimestamp(a.timestamp),
      );

    // Detect ratio changes - build a map of date -> ratios used that day
    interface RatioSnapshot {
      cpRatios: Record<string, number | undefined>;
      correctionFactor: number | undefined;
    }

    const ratioByDay = new Map<string, RatioSnapshot>();
    for (const entry of allEntriesInPeriod) {
      const dayKey = format(
        new Date(this._getTimestamp(entry.timestamp)),
        "yyyy-MM-dd",
      );
      if (!ratioByDay.has(dayKey)) {
        ratioByDay.set(dayKey, {
          cpRatios: {},
          correctionFactor: entry.correctionFactorUsed,
        });
      }
      const snapshot = ratioByDay.get(dayKey)!;
      if (entry.cpRatioUsed !== undefined && entry.periodTag) {
        const ratioKey =
          PERIOD_TAG_INFO[entry.periodTag]?.ratioFrom || entry.periodTag;
        if (!snapshot.cpRatios[ratioKey]) {
          snapshot.cpRatios[ratioKey] = entry.cpRatioUsed;
        }
      }
      if (
        entry.correctionFactorUsed !== undefined &&
        !snapshot.correctionFactor
      ) {
        snapshot.correctionFactor = entry.correctionFactorUsed;
      }
    }

    // Helper to get entry data for a day/period/index combination
    const getEntryField = (
      dayOffset: number,
      period: PeriodTag,
      entryIndex: number,
      field: string,
      timeCategory?: "3am" | "night" | "early",
    ) => {
      const dayEntries = this._getEntriesForDay(dayOffset);

      // For 3AM period, filter by time category
      let periodEntries: LogEntry[];
      if (period === "3AM" && timeCategory) {
        periodEntries = dayEntries.filter(
          (e) =>
            e.periodTag === period &&
            e.timestamp &&
            this._get3amTimeCategory(e.timestamp) === timeCategory,
        );
      } else {
        periodEntries = dayEntries.filter((e) => e.periodTag === period);
      }

      const entry = periodEntries[entryIndex];

      // For true 3am column (3:00-3:59) with no DAFNE entry, fall back to closest CGM reading
      if (!entry && period === "3AM" && timeCategory === "3am") {
        const cgmReading = this._get3amCgmReading(dayOffset);
        if (cgmReading) {
          switch (field) {
            case DAFNE_LABELS.TIME:
              return html`<span class="cgm-fallback"
                >${format(
                  new Date(this._getTimestamp(cgmReading.timestamp)),
                  "HH:mm",
                )}</span
              >`;
            case DAFNE_LABELS.GL:
              return html`<span
                class="cgm-fallback ${this._getGlClass(cgmReading.glucose)}"
                >${cgmReading.glucose.toFixed(1)}</span
              >`;
            default:
              return html`<span class="empty">-</span>`;
          }
        }
      }

      if (!entry) return html`<span class="empty">-</span>`;

      switch (field) {
        case DAFNE_LABELS.TIME:
          return entry.timestamp
            ? format(new Date(this._getTimestamp(entry.timestamp)), "HH:mm")
            : "-";
        case DAFNE_LABELS.GL:
          return entry.bloodGlucose
            ? html`<span class=${this._getGlClass(entry.bloodGlucose)}
                >${entry.bloodGlucose.toFixed(1)}</span
              >`
            : "-";
        case DAFNE_LABELS.CP:
          return entry.carbs ? `${entry.carbs}` : "-";
        case DAFNE_LABELS.QUICK:
          const qa = getQATotal(entry);
          return qa !== undefined
            ? formatInsulinDose(entry.qaBase, entry.qaCorrection)
            : "-";
        case DAFNE_LABELS.LONG:
          return entry.biBase !== undefined ? `${entry.biBase}` : "-";
        default:
          return "-";
      }
    };

    // Render ratio header showing current settings
    const renderRatioHeader = () => {
      const hasRatios =
        ratioPeriods.some((p) => settings.cpRatios[p] != null) ||
        settings.correctionFactor != null;

      if (!hasRatios) return "";

      return html`
        <div class="ratio-header">
          <span class="ratio-title">Current Ratios:</span>
          ${ratioPeriods.map((period) => {
            const gramsPerUnit = settings.cpRatios[period];
            if (gramsPerUnit == null) return "";
            return html`
              <span class="ratio-item">
                <span class="ratio-period"
                  >${PERIOD_TAG_INFO[period]?.label || period}:</span
                >
                <span class="ratio-value"
                  >${formatRatioDisplay(gramsPerUnit)}</span
                >
              </span>
            `;
          })}
          ${settings.correctionFactor != null
            ? html`
                <span class="ratio-item correction-item">
                  <span class="ratio-period">ISF:</span>
                  <span class="ratio-value"
                    >${settings.correctionFactor} mmol/L/u</span
                  >
                </span>
              `
            : ""}
        </div>
      `;
    };

    // Render a ratio change row in the table
    const renderRatioRow = (snapshot: RatioSnapshot, colCount: number) => {
      // Use centralized conversion from config/dafne/insulin.ts
      const formatRatioValue = (gramsPerUnit: number) => {
        const unitsPerCp = gramsPerUnitToUnitsPerCP(gramsPerUnit);
        return Number.isInteger(unitsPerCp)
          ? unitsPerCp.toString()
          : unitsPerCp.toFixed(1);
      };

      const ratioText = ratioPeriods
        .filter((p) => snapshot.cpRatios[p] != null)
        .map(
          (p) =>
            `${PERIOD_TAG_INFO[p]?.label?.charAt(0) || p}: ${formatRatioValue(snapshot.cpRatios[p]!)}u`,
        )
        .join(" | ");

      const correctionText =
        snapshot.correctionFactor != null
          ? `ISF: ${snapshot.correctionFactor}`
          : "";

      return html`
        <tr class="ratio-row">
          <td class="ratio-label" colspan="2">Ratios Used</td>
          ${ratioText
            ? html`<td colspan="${Math.floor((colCount - 1) / 2)}">
                ${ratioText}
              </td>`
            : ""}
          ${correctionText
            ? html`<td
                class="correction-cell"
                colspan="${colCount - Math.floor((colCount - 1) / 2) - 1}"
              >
                ${correctionText}
              </td>`
            : html`<td
                colspan="${colCount - Math.floor((colCount - 1) / 2) - 1}"
              ></td>`}
        </tr>
      `;
    };

    // Track previous day's ratios to detect changes
    let prevRatioSnapshot: RatioSnapshot | null = null;

    return html`
      <div class="diary-section">
        <h3>DAFNE Diary - ${this.viewPeriod} Days</h3>
        ${renderRatioHeader()}
        <div class="table-scroll">
          <table class="dafne-diary">
            <thead>
              <tr>
                <th class="date-col">Date</th>
                <th class="field-col"></th>
                ${columns.map(
                  (col) => html`<th class="period-col">${col.label}</th>`,
                )}
              </tr>
            </thead>
            <tbody>
              ${Array.from({ length: days }, (_, dayOffset) => {
                const dayDate = subDays(this.referenceDate, dayOffset);
                const dayKey = format(dayDate, "yyyy-MM-dd");
                const dayLabel =
                  dayOffset === 0
                    ? "Today"
                    : dayOffset === 1
                      ? "Yesterday"
                      : format(dayDate, "EEE");
                const dateLabel = format(dayDate, "d MMM");

                // Check if ratios changed from previous day
                const currentSnapshot = ratioByDay.get(dayKey);
                let showRatioChange = false;

                if (currentSnapshot && prevRatioSnapshot) {
                  // Check if any ratio differs
                  const cpChanged = ratioPeriods.some(
                    (p) =>
                      currentSnapshot.cpRatios[p] !==
                        prevRatioSnapshot!.cpRatios[p] &&
                      (currentSnapshot.cpRatios[p] != null ||
                        prevRatioSnapshot!.cpRatios[p] != null),
                  );
                  const corrChanged =
                    currentSnapshot.correctionFactor !==
                      prevRatioSnapshot.correctionFactor &&
                    (currentSnapshot.correctionFactor != null ||
                      prevRatioSnapshot.correctionFactor != null);

                  showRatioChange = cpChanged || corrChanged;
                }

                // Update previous snapshot for next iteration
                if (currentSnapshot) {
                  prevRatioSnapshot = currentSnapshot;
                }

                return html`
                  ${showRatioChange && currentSnapshot
                    ? renderRatioRow(currentSnapshot, columns.length)
                    : ""}
                  ${dataRows.map(
                    (field, fieldIdx) => html`
                      <tr
                        class=${fieldIdx === 0
                          ? "day-start"
                          : fieldIdx === dataRows.length - 1
                            ? "day-end"
                            : ""}
                      >
                        ${fieldIdx === 0
                          ? html`
                              <td
                                class="date-cell"
                                rowspan="${dataRows.length}"
                              >
                                <div class="day-name">${dayLabel}</div>
                                <div class="day-date">${dateLabel}</div>
                              </td>
                            `
                          : ""}
                        <td class="field-label ${field.toLowerCase()}">
                          ${field}
                        </td>
                        ${columns.map(
                          (col) => html`
                            <td class="data-cell ${field.toLowerCase()}">
                              ${getEntryField(
                                dayOffset,
                                col.period,
                                col.index,
                                field,
                                col.timeCategory,
                              )}
                            </td>
                          `,
                        )}
                      </tr>
                    `,
                  )}
                  ${this._renderDayNotes(dayOffset, columns.length + 2)}
                `;
              })}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  private _renderDayNotes(dayOffset: number, colCount: number) {
    const notes = this._getNotesForDay(dayOffset);
    const isOwnData = !this.dataAdapter; // No adapter means viewing own data
    const canAddNotes = isOwnData || this.dataAdapter?.canAddNotes;

    // Show if there are notes OR if user can add notes
    if (notes.length === 0 && !canAddNotes) {
      return "";
    }

    return html`
      <tr class="day-notes-row">
        <td colspan="${colCount}">
          <div class="day-notes-container">
            ${notes.map(
              (note) => html`
                <div class="day-note ${note.type}-note">
                  <div class="day-note-header">
                    <span class="note-type">
                      <sl-icon
                        name="${note.type === "clinic" ? "hospital" : "person"}"
                      ></sl-icon>
                      ${note.type === "clinic"
                        ? "Clinic Note"
                        : "Personal Note"}
                    </span>
                    ${note.authorName
                      ? html`<span class="note-author"
                          >by ${note.authorName}</span
                        >`
                      : ""}
                    <span class="note-date"
                      >${format(new Date(note.createdAt), "d MMM HH:mm")}</span
                    >
                  </div>
                  <div class="day-note-content">${note.content}</div>
                </div>
              `,
            )}
            ${canAddNotes
              ? html`
                  <sl-button
                    size="small"
                    variant="text"
                    class="add-note-btn"
                    @click=${() => this._openAddNoteDialog(dayOffset)}
                  >
                    <sl-icon slot="prefix" name="plus-circle"></sl-icon>
                    ${isOwnData ? "Add Note" : "Add Clinic Note"}
                  </sl-button>
                `
              : ""}
          </div>
        </td>
      </tr>
    `;
  }

  private _renderDailyBreakdown() {
    if (this.allReadings.length === 0) {
      return ""; // No CGM data to show
    }

    const days = parseInt(this.viewPeriod);

    return html`
      <div class="daily-breakdown">
        <div class="summary-header">
          <h3>CGM Daily Summary</h3>
        </div>
        <div class="day-header-row">
          <div>Date</div>
          <div>Trend</div>
          <div>Avg</div>
          <div>TIR</div>
          <div>Low</div>
          <div>High</div>
        </div>
        ${Array.from({ length: days }, (_, i) => this._renderDayRow(i))}
      </div>
    `;
  }

  private _renderDayRow(dayOffset: number) {
    const stats = this._getDayStats(dayOffset);
    const hasData = stats.readings.length > 0;
    const isSelected = this.selectedDay === dayOffset;
    const isToday = dayOffset === 0;

    return html`
      <div
        class="day-row ${isSelected ? "selected" : ""} ${!hasData
          ? "no-data"
          : ""}"
        @click=${() => this._toggleDayDetail(dayOffset, hasData)}
      >
        <div class="day-date">
          ${isToday ? "Today" : format(stats.date, "d MMM")}
          <span class="weekday">${format(stats.date, "EEE")}</span>
        </div>
        <div class="day-sparkline">
          ${hasData ? this._renderMiniSparkline(stats.readings) : ""}
        </div>
        <div class="day-stat avg">${hasData ? stats.avg.toFixed(1) : "-"}</div>
        <div class="day-stat tir">${hasData ? `${stats.tir}%` : "-"}</div>
        <div class="day-stat hypo">${hasData ? stats.hypos : "-"}</div>
        <div class="day-stat high">${hasData ? stats.highs : "-"}</div>
      </div>
      ${isSelected ? this._renderDayDetail(stats) : ""}
    `;
  }

  private _renderMiniSparkline(readings: CgmReading[]) {
    if (readings.length < 2) return "";

    // Create a simple SVG sparkline
    const width = 100;
    const height = 24;
    const padding = 2;

    const values = readings.map((r) => r.glucose);
    const min = Math.min(...values, 2);
    const max = Math.max(...values, 20);
    const range = max - min || 1;

    const points = readings.map((r, i) => {
      const x = padding + (i / (readings.length - 1)) * (width - 2 * padding);
      const y =
        height - padding - ((r.glucose - min) / range) * (height - 2 * padding);
      return `${x},${y}`;
    });

    // Target range band (4-10 mmol/L)
    const targetTop =
      height - padding - ((10 - min) / range) * (height - 2 * padding);
    const targetBottom =
      height - padding - ((4 - min) / range) * (height - 2 * padding);

    return html`
      <svg
        width="100%"
        height="${height}"
        viewBox="0 0 ${width} ${height}"
        preserveAspectRatio="none"
      >
        <rect
          x="${padding}"
          y="${Math.max(padding, targetTop)}"
          width="${width - 2 * padding}"
          height="${Math.min(height - 2 * padding, targetBottom - targetTop)}"
          fill="#d1e7dd"
          opacity="0.5"
        />
        <polyline
          fill="none"
          stroke="#0d6efd"
          stroke-width="1.5"
          points="${points.join(" ")}"
        />
      </svg>
    `;
  }

  private _renderDayDetail(stats: DayStats) {
    // Show hourly summary
    const hourlyData = new Map<
      number,
      { readings: CgmReading[]; avg: number }
    >();

    for (const r of stats.readings) {
      const hour = new Date(this._getTimestamp(r.timestamp)).getHours();
      if (!hourlyData.has(hour)) {
        hourlyData.set(hour, { readings: [], avg: 0 });
      }
      hourlyData.get(hour)!.readings.push(r);
    }

    // Calculate hourly averages
    for (const [, data] of hourlyData) {
      const values = data.readings.map((r) => r.glucose);
      data.avg = values.reduce((a, b) => a + b, 0) / values.length;
    }

    // Sort by hour
    const sortedHours = Array.from(hourlyData.entries()).sort(
      (a, b) => a[0] - b[0],
    );

    return html`
      <div class="day-detail">
        <div class="summary-header">
          <h3>Hourly Breakdown</h3>
          <sl-badge variant="neutral"
            >${stats.readings.length} readings</sl-badge
          >
        </div>
        <table class="readings-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Readings</th>
              <th>Avg</th>
              <th>Range</th>
            </tr>
          </thead>
          <tbody>
            ${sortedHours.map(([hour, data]) => {
              const values = data.readings.map((r) => r.glucose);
              const min = Math.min(...values);
              const max = Math.max(...values);
              return html`
                <tr>
                  <td>${hour.toString().padStart(2, "0")}:00</td>
                  <td>${data.readings.length}</td>
                  <td>
                    <span
                      class="reading-badge ${this._getReadingClass(data.avg)}"
                    >
                      ${data.avg.toFixed(1)}
                    </span>
                  </td>
                  <td>${min.toFixed(1)} - ${max.toFixed(1)}</td>
                </tr>
              `;
            })}
          </tbody>
        </table>
      </div>
    `;
  }

  render() {
    const adapter = this.dataAdapter;
    const pageTitle = adapter?.title || "History";
    const pageSubtitle = adapter?.subtitle;
    const hasBackButton = !!adapter?.onBack;

    if (this.loading) {
      return html`
        <app-shell pageTitle=${pageTitle}>
          ${hasBackButton
            ? html`
                <div class="back-header">
                  <sl-button variant="text" @click=${() => adapter?.onBack?.()}>
                    <sl-icon slot="prefix" name="arrow-left"></sl-icon>
                    Back
                  </sl-button>
                </div>
              `
            : ""}
          <div class="loading">
            <sl-spinner style="font-size: 2rem;"></sl-spinner>
          </div>
        </app-shell>
      `;
    }

    const hasAnyData = this.allReadings.length > 0 || this.entries.length > 0;

    if (!hasAnyData) {
      return html`
        <app-shell pageTitle=${pageTitle}>
          ${hasBackButton
            ? html`
                <div class="back-header">
                  <sl-button variant="text" @click=${() => adapter?.onBack?.()}>
                    <sl-icon slot="prefix" name="arrow-left"></sl-icon>
                    Back
                  </sl-button>
                </div>
              `
            : ""}
          <div class="page-header">
            <h1>${adapter?.title || "Glucose History"}</h1>
            <p>${pageSubtitle || "CGM data analysis and trends"}</p>
          </div>
          <div class="no-data">
            <sl-icon name="graph-up"></sl-icon>
            <p>No data available</p>
            ${!adapter
              ? html`<p>
                  <a href="/upload">Import LibreView data</a> or
                  <a href="/add">add diary entries</a>
                </p>`
              : ""}
          </div>
        </app-shell>
      `;
    }

    const totalReadings = this.allReadings.length;
    const totalEntries = this.entries.length;
    const dateRangeText = this.dataDateRange
      ? `${format(this.dataDateRange.start, "d MMM yyyy")} - ${format(this.dataDateRange.end, "d MMM yyyy")}`
      : "";

    const dataInfo: string[] = [];
    if (totalReadings > 0)
      dataInfo.push(`${totalReadings.toLocaleString()} CGM readings`);
    if (totalEntries > 0) dataInfo.push(`${totalEntries} diary entries`);

    return html`
      <app-shell pageTitle=${pageTitle}>
        ${hasBackButton
          ? html`
              <div class="back-header">
                <sl-button variant="text" @click=${() => adapter?.onBack?.()}>
                  <sl-icon slot="prefix" name="arrow-left"></sl-icon>
                  Back
                </sl-button>
              </div>
            `
          : ""}
        <div class="page-header">
          <h1>${adapter?.title || "Glucose History"}</h1>
          <p>
            ${pageSubtitle || dataInfo.join(", ")}
            ${dateRangeText && !pageSubtitle ? `(${dateRangeText})` : ""}
          </p>
        </div>

        <div class="view-tabs">
          <sl-button
            size="small"
            variant=${this.viewPeriod === "3" ? "primary" : "default"}
            @click=${() => this._handleViewChange("3")}
            title="DAFNE assessment: 2+ days consistency before ratio changes"
          >
            3 Days
          </sl-button>
          <sl-button
            size="small"
            variant=${this.viewPeriod === "7" ? "primary" : "default"}
            @click=${() => this._handleViewChange("7")}
          >
            7 Days
          </sl-button>
          <sl-button
            size="small"
            variant=${this.viewPeriod === "14" ? "primary" : "default"}
            @click=${() => this._handleViewChange("14")}
          >
            14 Days
          </sl-button>
          <sl-button
            size="small"
            variant=${this.viewPeriod === "30" ? "primary" : "default"}
            @click=${() => this._handleViewChange("30")}
          >
            30 Days
          </sl-button>
        </div>

        <div class="time-filters">
          <span class="label">Time:</span>
          <sl-button
            size="small"
            variant=${this.timeFilter === "all" ? "primary" : "default"}
            @click=${() => this._handleTimeFilter("all")}
          >
            All Day
          </sl-button>
          <sl-button
            size="small"
            variant=${this.timeFilter === "morning" ? "primary" : "default"}
            @click=${() => this._handleTimeFilter("morning")}
          >
            Morning
          </sl-button>
          <sl-button
            size="small"
            variant=${this.timeFilter === "afternoon" ? "primary" : "default"}
            @click=${() => this._handleTimeFilter("afternoon")}
          >
            Afternoon
          </sl-button>
          <sl-button
            size="small"
            variant=${this.timeFilter === "evening" ? "primary" : "default"}
            @click=${() => this._handleTimeFilter("evening")}
          >
            Evening
          </sl-button>
          <sl-button
            size="small"
            variant=${this.timeFilter === "night" ? "primary" : "default"}
            @click=${() => this._handleTimeFilter("night")}
          >
            Night
          </sl-button>
        </div>

        ${this._renderStats()} ${this._renderPatterns()} ${this._renderGraph()}
        ${this._renderDiaryTable()} ${this._renderDailyBreakdown()}

        <!-- Add Note Dialog -->
        <sl-dialog
          label="${this.dataAdapter ? "Add Clinic Note" : "Add Note"}"
          ?open=${this.showNoteDialog}
          @sl-request-close=${() => this._closeNoteDialog()}
        >
          ${this.editingNoteDay
            ? html`<p class="note-dialog-date">
                ${this.dataAdapter
                  ? html`Clinic note for ${this.dataAdapter.title} — `
                  : "Note for "}
                ${format(
                  new Date(this.editingNoteDay + "T12:00:00"),
                  "EEEE, d MMMM yyyy",
                )}
              </p>`
            : ""}
          <sl-textarea
            placeholder="${this.dataAdapter
              ? "Add a clinic note for this patient's day..."
              : "Add your note about this day..."}"
            rows="4"
            .value=${this.noteText}
            @sl-input=${(e: Event) =>
              (this.noteText = (e.target as HTMLTextAreaElement).value)}
          ></sl-textarea>
          <div slot="footer">
            <sl-button
              variant="default"
              @click=${() => this._closeNoteDialog()}
            >
              Cancel
            </sl-button>
            <sl-button
              variant="primary"
              ?loading=${this.savingNote}
              ?disabled=${!this.noteText.trim()}
              @click=${() => this._handleSaveNote()}
            >
              Save Note
            </sl-button>
          </div>
        </sl-dialog>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "history-page": HistoryPage;
  }
}
